import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';
import { apiFetch } from '../services/api';
import { cardImage } from '../utils/productImages';

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState({ products: [], posts: [] });
  const searchRef = useRef(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close popups on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setAccountOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // UX-01: Enter previously navigated to /search?q=... which had no
  // route, taking users to the 404 page. The inline dropdown below
  // already shows product matches, so swallow the form submit
  // and let users click a result instead.
  const handleSearch = (e) => {
    e.preventDefault();
  };

  // Lazy-load search index when search opens.
  // Blog posts are still not in the search index. The blog is linked from the
  // nav again, so the reason they were left out has gone, but the rendering
  // block that displayed post results went with them and putting it back is a
  // separate change - see git history.
  useEffect(() => {
    if (!searchOpen) return;
    if (searchIndex.products.length) return;
    apiFetch('/api/products')
      .then((r) => r.json())
      .then((prods) => setSearchIndex({ products: Array.isArray(prods) ? prods : [], posts: [] }))
      .catch(() => {});
  }, [searchOpen, searchIndex.products.length]);

  const q = searchQuery.trim().toLowerCase();
  const matchedProducts = q
    ? searchIndex.products
        .filter((p) => p.active !== false)
        .filter((p) => (p.name || '').toLowerCase().includes(q) || (p.short_description || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q))
        .slice(0, 5)
    : searchIndex.products.filter((p) => p.active !== false).slice(0, 5);
  const hasSuggestions = matchedProducts.length > 0;

  const closeSearch = () => { setSearchOpen(false); setSearchQuery(''); };

  const isHome = location.pathname === '/';

  // Seven destinations plus one primary CTA, deliberately short so the nav
  // reads as a decision path rather than a site index.
  //
  // Deliberately NOT linked here:
  //  - Samples: free posted samples were discontinued; /samples now
  //    301-redirects to /colours.
  //  - Admin: /admin/login still works when typed directly, but must never be
  //    linked from a public surface (it is also noindex + robots-disallowed).
  //
  // Pergolas points at the products listing pre-filtered to the pergola roof
  // types (louvered / flat-blade / retractable) - unfiltered it also lists
  // awnings and accessories. Colours now has its own page (/colours); it used
  // to jump to a section on the awnings page.
  const navLinks = [
    { to: '/products/awnings', label: 'Awnings' },
    { to: '/products?filter=pergolas', label: 'Pergolas' },
    { to: '/colours', label: 'Colours' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/about', label: 'About' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ];
  const ctaLink = { to: '/request-quote', label: 'Get a Quote' };

  // Nav targets carry query strings (?filter=pergolas) and hashes (#colours),
  // so compare against the path portion only for the active state.
  const isActive = (to) => location.pathname === to.split(/[?#]/)[0];

  return (
    <>
    <nav className={`nav ${scrolled ? 'nav--scrolled' : ''} ${isHome && !scrolled ? 'nav--transparent' : ''} ${mobileOpen ? 'nav--menu-open' : ''}`}>
      <div className="nav__inner">
        {/* Logo */}
        <Link to="/" className="nav__logo">
          <img
            src={isHome && !scrolled ? '/images/asg-logo-white.webp' : '/images/asg-logo-transparent.webp'}
            alt="ASG Shading - Architectural Shading Melbourne"
            className="nav__logo-img"
            width="150"
            height="100"
          />
        </Link>

        {/* Desktop links */}
        <div className="nav__links">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`nav__link ${isActive(link.to) ? 'nav__link--active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
          <Link to={ctaLink.to} className="nav__link nav__link--cta">{ctaLink.label}</Link>
        </div>

        {/* Right side icons */}
        <div className="nav__actions">
          {/* Search */}
          <div className="nav__action-wrap" ref={searchRef}>
            <button
              className="nav__icon-btn"
              onClick={() => { setSearchOpen(!searchOpen); }}
              aria-label="Search"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
            {searchOpen && (
              <>
                <div className="nav__search-backdrop" onClick={closeSearch} />
                <div className="nav__popup nav__popup--search">
                  <form onSubmit={handleSearch} className="nav__search-form">
                    <svg className="nav__search-icon" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                      type="text"
                      className="nav__search-input"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                    {searchQuery && (
                      <button type="button" className="nav__search-clear" onClick={() => setSearchQuery('')} aria-label="Clear">
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>
                      </button>
                    )}
                  </form>
                  {hasSuggestions && (
                    <div className="nav__search-results">
                      {matchedProducts.length > 0 && (
                        <div className="nav__search-group">
                          <p className="nav__search-group-label">Products</p>
                          {matchedProducts.map((p) => (
                            <Link
                              key={p._id || p.slug}
                              to={`/products/${p.slug || p._id}`}
                              className="nav__search-result"
                              onClick={closeSearch}
                            >
                              {cardImage(p) ? (
                                <img src={cardImage(p)} alt="" className="nav__search-thumb" loading="lazy" />
                              ) : (
                                <span className="nav__search-thumb nav__search-thumb--empty" />
                              )}
                              <span className="nav__search-result-text">
                                <span className="nav__search-result-title">{p.name}</span>
                                {p.category && <span className="nav__search-result-meta">{p.category}</span>}
                              </span>
                            </Link>
                          ))}
                        </div>
                      )}
                      {/* The blog results group was removed here alongside
                          unlinking the blog - see the note on the search index
                          effect above. */}
                    </div>
                  )}
                  {q && !hasSuggestions && (
                    <div className="nav__search-empty">No matches for "{searchQuery}"</div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Account - an icon beside search rather than words in the main
              header. An account is never required to buy: checkout runs on
              the server's customerAuthOptional path, so signing in only
              prefills details. Phones reach the same links from the menu. */}
          <div className="nav__action-wrap nav__account" ref={accountRef}>
            <button
              type="button"
              className="nav__icon-btn"
              onClick={() => setAccountOpen((o) => !o)}
              aria-label={user ? 'Your account' : 'Sign in or create an account'}
              aria-expanded={accountOpen}
              aria-haspopup="menu"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" strokeLinecap="round" />
              </svg>
            </button>
            {accountOpen && (
              <div className="nav__popup nav__popup--account" role="menu">
                {user ? (
                  <>
                    <div className="nav__popup-header">
                      <strong>{user.full_name ? user.full_name.split(' ')[0] : 'Your account'}</strong>
                      <span>{user.email}</span>
                    </div>
                    <Link to="/account" className="nav__popup-link" role="menuitem">My account</Link>
                    <button
                      type="button"
                      className="nav__popup-link"
                      role="menuitem"
                      onClick={() => { setAccountOpen(false); logout(); navigate('/'); }}
                    >Sign out</button>
                  </>
                ) : (
                  <>
                    <Link to="/sign-in" className="nav__popup-link" role="menuitem">Sign in</Link>
                    <Link to="/sign-up" className="nav__popup-link" role="menuitem">Create account</Link>
                    <div className="nav__popup-divider" />
                    <p className="nav__popup-link nav__popup-link--muted">No account needed to order.</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className={`nav__hamburger ${mobileOpen ? 'nav__hamburger--open' : ''}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

    </nav>

      {/* Mobile menu - outside nav to avoid height clipping */}
      <div className={`nav__mobile ${mobileOpen ? 'nav__mobile--open' : ''}`}>
        {/* Mirrors the desktop structure exactly: same five links, same CTA. */}
        {navLinks.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className={`nav__mobile-link ${isActive(link.to) ? 'nav__mobile-link--active' : ''}`}
          >
            {link.label}
          </Link>
        ))}
        <Link to={ctaLink.to} className="nav__mobile-link nav__mobile-link--cta">{ctaLink.label}</Link>
        <div className="nav__mobile-divider"></div>
        {user ? (
          <>
            <Link to="/account" className="nav__mobile-link nav__mobile-link--small">My account ({user.full_name.split(' ')[0]})</Link>
            <button
              type="button"
              className="nav__mobile-link nav__mobile-link--small nav__mobile-link--btn"
              onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
            >Sign out</button>
          </>
        ) : (
          <>
            <Link to="/sign-in" className="nav__mobile-link nav__mobile-link--small">Sign in</Link>
            <Link to="/sign-up" className="nav__mobile-link nav__mobile-link--small">Create account</Link>
          </>
        )}
      </div>
    </>
  );
}

export default Navbar;
